/**
 * OCR Service - Extract data from Algerian Registre de Commerce (RC) documents
 * 
 * Updated to match ACTUAL official RC format from CNRC (Centre National du Registre de Commerce)
 * 
 * Supports:
 * - French RC documents (official translation)
 * - Arabic RC documents (original)
 * - Mixed language documents
 * - PDF and image formats
 * - NAE activity codes (607XXX format)
 * - Multiple activity descriptions
 */

import Tesseract from 'tesseract.js';
import type { RCExtractedData, FieldLocation } from '@lebusiness/types';

interface OCRResult {
  success: boolean;
  data?: RCExtractedData;
  error?: string;
  confidence: number;
  fieldLocations?: FieldLocation[];
  pages?: { imageUrl: string; text: string }[];
}

/**
 * Wilaya code to name mapping (all 58 wilayas of Algeria)
 */
const WILAYA_MAP: Record<string, string> = {
  '01': '01-Adrar',
  '02': '02-Chlef',
  '03': '03-Laghouat',
  '04': '04-Oum El Bouaghi',
  '05': '05-Batna',
  '06': '06-Béjaïa',
  '07': '07-Biskra',
  '08': '08-Béchar',
  '09': '09-Blida',
  '10': '10-Bouira',
  '11': '11-Tamanrasset',
  '12': '12-Tébessa',
  '13': '13-Tlemcen',
  '14': '14-Tiaret',
  '15': '15-Tizi Ouzou',
  '16': '16-Alger',
  '17': '17-Djelfa',
  '18': '18-Jijel',
  '19': '19-Sétif',
  '20': '20-Saïda',
  '21': '21-Skikda',
  '22': '22-Sidi Bel Abbès',
  '23': '23-Annaba',
  '24': '24-Guelma',
  '25': '25-Constantine',
  '26': '26-Médéa',
  '27': '27-Mostaganem',
  '28': "28-M'Sila",
  '29': '29-Mascara',
  '30': '30-Ouargla',
  '31': '31-Oran',
  '32': '32-El Bayadh',
  '33': '33-Illizi',
  '34': '34-Bordj Bou Arréridj',
  '35': '35-Boumerdès',
  '36': '36-El Tarf',
  '37': '37-Tindouf',
  '38': '38-Tissemsilt',
  '39': '39-El Oued',
  '40': '40-Khenchela',
  '41': '41-Souk Ahras',
  '42': '42-Tipaza',
  '43': '43-Mila',
  '44': '44-Aïn Defla',
  '45': '45-Naâma',
  '46': '46-Aïn Témouchent',
  '47': '47-Ghardaïa',
  '48': '48-Relizane',
  '49': '49-Timimoun',
  '50': '50-Bordj Badji Mokhtar',
  '51': '51-Ouled Djellal',
  '52': '52-Béni Abbès',
  '53': '53-In Salah',
  '54': '54-In Guezzam',
  '55': '55-Touggourt',
  '56': '56-Djanet',
  '57': "57-El M'Ghair",
  '58': '58-El Meniaa',
};

/**
 * NAE Activity Codes (Code de l'activité)
 * Based on real RC documents showing codes like 607061, 607069, 607014
 */
const NAE_ACTIVITY_DESCRIPTIONS: Record<string, { fr: string; ar: string }> = {
  '607061': {
    fr: 'Conseils dans le domaine de la télécommunication',
    ar: 'استشارة في مجال المواصلات السلكية واللاسلكية',
  },
  '607001': {
    fr: 'Bureau d\'étude et de conseil en informatique (consulting)',
    ar: 'مكتب الدراسات والإستشارة في الإعلام الآلي (كونسولتينج)',
  },
  '607069': {
    fr: 'Programmation de système informatique',
    ar: 'برمجة أنظمة الإعلام الآلي',
  },
  '607014': {
    fr: 'Entreprise de formation d\'enseignement divers',
    ar: 'مؤسسة تعليم اللغات',
  },
};

export class OCRService {
  /**
   * Extract data from RC document
   * Now handles real CNRC format with NAE codes and activity descriptions
   */
  static async extractFromRC(file: File): Promise<OCRResult> {
    try {
      const imageUrl = await this.fileToDataURL(file);

      // Perform OCR in both French and Arabic with better settings
      const [frenchResult, arabicResult] = await Promise.all([
        this.performOCR(imageUrl, 'fra'),
        this.performOCR(imageUrl, 'ara'),
      ]);

      const frenchText = frenchResult.data.text;
      const arabicText = arabicResult.data.text;

      // Parse using improved extraction patterns
      const extractedData = this.parseRCTextImproved(frenchText, arabicText);

      // Calculate confidence
      const confidence = (frenchResult.data.confidence + arabicResult.data.confidence) / 200;
      extractedData.confidence_score = confidence;

      // Extract field locations for annotations
      const fieldLocations = this.extractFieldLocations(frenchResult, arabicResult);

      return {
        success: true,
        data: extractedData,
        confidence,
        fieldLocations,
        pages: [{ imageUrl, text: frenchText + '\n\n' + arabicText }],
      };
    } catch (error) {
      console.error('OCR extraction failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'OCR extraction failed',
        confidence: 0,
      };
    }
  }

  /**
   * Improved parsing based on actual RC document format
   */
  private static parseRCTextImproved(frenchText: string, arabicText: string): RCExtractedData {
    const data: RCExtractedData = {};

    // 1. RC Number - Format: XX/XX-XXXXXXX (e.g., 22/00-0166475)
    const rcPatterns = [
      /N°?\s*d['']enregistrement\s*[:\s]+(\d{2}\/\d{2}[–-]\d{6,8})/i,
      /(\d{2}\/\d{2}[–-]\d{6,8})/,
      /رقم\s*التسجيل\s*[:\s]*(\d{2}\/\d{2}[–-]\d{6,8})/,
    ];

    for (const pattern of rcPatterns) {
      const match = frenchText.match(pattern) || arabicText.match(pattern);
      if (match) {
        data.rc_number = match[1].replace('–', '-'); // Normalize dash
        // Extract wilaya code from RC
        const wilayaCode = match[1].substring(0, 2);
        if (WILAYA_MAP[wilayaCode]) {
          data.wilaya = WILAYA_MAP[wilayaCode];
        }
        break;
      }
    }

    // 2. Serial Number - Format: XXXXXXXXX (e.g., 200246154)
    const serialPatterns = [
      /N°\s*de\s*série\s*[:\s]+(\d{9,10})/i,
      /الرقم\s*التسلسلي\s*[:\s]*(\d{9,10})/,
      /(\d{9,10})\s*$/m, // Often at end of line
    ];

    for (const pattern of serialPatterns) {
      const match = frenchText.match(pattern) || arabicText.match(pattern);
      if (match) {
        data.serial_number = match[1];
        break;
      }
    }

    // 3. Company Name (Prénoms et nom / الإسم واللقب)
    const namePatternsFr = [
      /Prénoms?\s+et\s+nom\s*[:\s]+([^\n]+)/i,
      /MEGHERBI\s+([^\n]+)/i,
      /nom\s*[:\s]+([A-Z][^\n]+)/i,
    ];

    for (const pattern of namePatternsFr) {
      const match = frenchText.match(pattern);
      if (match) {
        data.company_name_fr = match[1].trim();
        break;
      }
    }

    const namePatternsAr = [
      /الإسم\s+واللقب\s*[:\s]*([^\n]+)/,
      /مغربي\s*([^\n]+)/,
    ];

    for (const pattern of namePatternsAr) {
      const match = arabicText.match(pattern);
      if (match) {
        data.company_name_ar = match[1].trim();
        break;
      }
    }

    // 4. NIF - Not always present in RC, but check
    const nifPatterns = [
      /NIF\s*[:\s]*(\d{15,20})/i,
      /رقم\s*التعريف\s*الجبائي\s*[:\s]*(\d{15,20})/,
    ];

    for (const pattern of nifPatterns) {
      const match = frenchText.match(pattern) || arabicText.match(pattern);
      if (match) {
        data.nif = match[1];
        break;
      }
    }

    // 5. NIS - Not always in RC
    const nisPatterns = [
      /NIS\s*[:\s]*(\d{15,20})/i,
      /رقم\s*التعريف\s*الإحصائي\s*[:\s]*(\d{15,20})/,
    ];

    for (const pattern of nisPatterns) {
      const match = frenchText.match(pattern) || arabicText.match(pattern);
      if (match) {
        data.nis = match[1];
        break;
      }
    }

    // 6. Legal Form (Personne Physique / شخص طبيعي)
    if (/Personne\s+Physique/i.test(frenchText) || /شخص\s*طبيعي/.test(arabicText)) {
      data.legal_form = 'EI';
      data.legal_form_fr = 'Personne Physique';
      data.legal_form_ar = 'شخص طبيعي';
    } else if (/SARL/i.test(frenchText)) {
      data.legal_form = 'SARL';
      data.legal_form_fr = 'SARL';
    } else if (/EURL/i.test(frenchText)) {
      data.legal_form = 'EURL';
      data.legal_form_fr = 'EURL';
    }

    // 7. Address (Domicile / عنوان الخاضع للبيع)
    const addressPatternsFr = [
      /Domicile\s*[:\s]+([^\n]+)/i,
      /N°\s*(\d+)\s+cité\s+([^,]+),\s*([^,]+),\s*([^\n]+)/i,
      /Adresse.*?[:\s]+([^\n]+)/i,
    ];

    for (const pattern of addressPatternsFr) {
      const match = frenchText.match(pattern);
      if (match) {
        data.address = match[0].includes('Domicile') ? match[1].trim() : match[0].trim();
        break;
      }
    }

    // 8. Business Address (Adresse du fonds commercial)
    const businessAddressPatterns = [
      /Adresse\s+du\s+fonds\s+commercial\s*[:\s]+([^\n]+(?:\n[^\n]+)?)/i,
      /عنوان\s+القاعدة\s+التجاري\s*[:\s]*([^\n]+)/,
    ];

    for (const pattern of businessAddressPatterns) {
      const match = frenchText.match(pattern) || arabicText.match(pattern);
      if (match) {
        data.business_address = match[1].trim();
        break;
      }
    }

    // 9. NAE Activity Codes (607061, 607001, 607069, 607014)
    const activityCodes: string[] = [];
    const activityDescriptionsFr: string[] = [];
    const activityDescriptionsAr: string[] = [];

    // Extract all 6-digit codes starting with 607
    const codePattern = /\b(6070\d{2})\b/g;
    let match;
    while ((match = codePattern.exec(frenchText)) !== null) {
      const code = match[1];
      if (!activityCodes.includes(code)) {
        activityCodes.push(code);
        if (NAE_ACTIVITY_DESCRIPTIONS[code]) {
          activityDescriptionsFr.push(NAE_ACTIVITY_DESCRIPTIONS[code].fr);
          activityDescriptionsAr.push(NAE_ACTIVITY_DESCRIPTIONS[code].ar);
        }
      }
    }

    if (activityCodes.length > 0) {
      data.activity_codes = activityCodes;
      data.activity_fr = activityDescriptionsFr.join(' ; ');
      data.activity_ar = activityDescriptionsAr.join(' ; ');
    }

    // Also try to extract activity descriptions directly from text
    const activityPatternsFr = [
      /(?:Activité|SERVICES)[:\s]+([^\n]+(?:\n[^\n]+)?)/i,
      /Conseils\s+dans\s+le\s+domaine[^\n]+/i,
      /Bureau\s+d['']étude[^\n]+/i,
    ];

    for (const pattern of activityPatternsFr) {
      const actMatch = frenchText.match(pattern);
      if (actMatch && !data.activity_fr) {
        data.activity_fr = actMatch[1] || actMatch[0];
        break;
      }
    }

    // 10. Capital (not always present for Personne Physique)
    const capitalPatterns = [
      /Capital\s*[:\s]*([\d\s,.]+)\s*(?:DA|DZD)/i,
      /رأس\s*المال\s*[:\s]*([\d\s,.]+)/,
    ];

    for (const pattern of capitalPatterns) {
      const match = frenchText.match(pattern) || arabicText.match(pattern);
      if (match) {
        const capitalStr = match[1].replace(/[\s,]/g, '');
        const capital = parseFloat(capitalStr);
        if (!isNaN(capital)) {
          data.capital = capital;
        }
        break;
      }
    }

    // 11. Registration Date (Date d'immatriculation)
    const datePatterns = [
      /Date\s+d['']immatriculation\s*[:\s]+(\d{2}\/\d{2}\/\d{4})/i,
      /تاريخ\s+التسجيل\s*[:\s]*(\d{2}\/\d{2}\/\d{4})/,
      /(\d{2}\/\d{2}\/\d{4})/,
    ];

    for (const pattern of datePatterns) {
      const match = frenchText.match(pattern) || arabicText.match(pattern);
      if (match) {
        data.registration_date = match[1];
        break;
      }
    }

    // 12. Activity Start Date (Date du début d'activité)
    const activityDatePatterns = [
      /Date\s+du\s+début\s+d['']activité\s*[:\s]+(\d{2}\/\d{2}\/\d{4})/i,
      /تاريخ\s+بداية\s+النشاط\s*[:\s]*(\d{2}\/\d{2}\/\d{4})/,
    ];

    for (const pattern of activityDatePatterns) {
      const match = frenchText.match(pattern) || arabicText.match(pattern);
      if (match) {
        data.activity_start_date = match[1];
        break;
      }
    }

    return data;
  }

  /**
   * Extract field locations for visual annotations
   */
  private static extractFieldLocations(
    frenchResult: Tesseract.RecognizeResult,
    arabicResult: Tesseract.RecognizeResult
  ): FieldLocation[] {
    const locations: FieldLocation[] = [];

    // Helper to create field location
    const addLocation = (
      field: string,
      text: string,
      bbox: any,
      confidence: number,
      label: string
    ) => {
      if (bbox && bbox.x0 !== undefined) {
        locations.push({
          field,
          text,
          bbox: {
            x: bbox.x0,
            y: bbox.y0,
            width: bbox.x1 - bbox.x0,
            height: bbox.y1 - bbox.y0,
          },
          confidence,
          label,
        });
      }
    };

    // Extract from French OCR
    frenchResult.data.words.forEach((word) => {
      const text = word.text;
      const conf = word.confidence;

      // RC Number
      if (/\d{2}\/\d{2}-\d{6,8}/.test(text)) {
        addLocation('rc_number', text, word.bbox, conf, 'RC Number');
      }

      // NIF
      if (/^\d{15,20}$/.test(text)) {
        addLocation('nif', text, word.bbox, conf, 'NIF');
      }

      // Activity codes
      if (/^607\d{3}$/.test(text)) {
        addLocation('activity_codes', text, word.bbox, conf, 'Code NAE');
      }
    });

    return locations;
  }

  /**
   * Perform OCR using Tesseract with optimized settings
   */
  private static async performOCR(
    imageUrl: string,
    language: 'fra' | 'ara'
  ): Promise<Tesseract.RecognizeResult> {
    const result = await Tesseract.recognize(imageUrl, language, {
      logger: (m) => {
        if (m.status === 'recognizing text') {
          console.log(`OCR Progress (${language}): ${Math.round(m.progress * 100)}%`);
        }
      },
    });

    return result;
  }

  /**
   * Convert file to data URL
   */
  private static fileToDataURL(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          resolve(e.target.result as string);
        } else {
          reject(new Error('Failed to read file'));
        }
      };
      reader.onerror = () => reject(new Error('File reading failed'));
      reader.readAsDataURL(file);
    });
  }
}
